import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import NoDataCreated from "../../../Components/NoDataCreated";
import PageLoader from "../../../Components/PageLoader";
import ErrorPage from "../../../Components/ErrorPage";
import { goToTop } from "../../../Components/goToTop";
import { useAppDispatch, useAppSelector } from "../../../Controllers/hooks";
import { ProjectSpendingTypes } from "../../../Controllers/Types/HeyTraderTypes/ProjectSpending/ProjectSpendingTypes";
import { getAllSpendingProjectList } from "../../../Controllers/Thunks/HeyTraderThunks";
import { addProjectForSpending } from "../../../Api/HeyTraderService/ProjectSpendingService";
import { useSelector } from "react-redux";
import { RootState } from "../../../Controllers/Store";
import { hasPermission } from "../../../Controllers/Selectors/permissionsSelector";
import SharedByAvatar from "../../../Components/SharedByAvatar";

const ProjectSpendingProjects: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const loadingPage = useAppSelector((state) => state.projectSpending.loader);
  const error = useAppSelector((state) => state.projectSpending.errorDisplay);

  // variable declartions with value set
  const [loading, setLoading] = useState<boolean>(false);

  //filter button actions
  const [projectsFilterState, setProjectsFilterState] = useState<"Current" | "Ongoing" | "Completed">("Current");

  //  for filter options
  const [projectsData, setProjectsData] = useState<ProjectSpendingTypes[]>([]);
  const [spendingProjectsData, setSpendingProjectsData] = useState<ProjectSpendingTypes[]>([]);
  const [filteredSpendingProjectsData, setFilteredSpendingProjectsData] = useState<ProjectSpendingTypes[]>([]);

  // for the resons
  const dispatch = useAppDispatch();

  // this is get data from state list and set projectlist
  const ProjectsList = useAppSelector((state) => state.projectSpending.spendingProjectList);

  //  model open and close
  const [showAddProjectsModal, setShowAddProjectsModal] = useState<boolean>(false);

  // check box project selected actions.
  const [selectedProjects, setSelectedProjects] = useState<ProjectSpendingTypes[]>([]);

  // Permision check
  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  const is_permision_add_project = useCanEdit("Project Spending", "add_project");

  // page load render action initstate
  useEffect(() => {
    setSpendingProjectsData(ProjectsList.filter((project) => project.is_spending_project_list === true));
    setProjectsData(ProjectsList.filter((project) => project.is_spending_project_list === false));
  }, [ProjectsList]);

  // for fetching data fro api tigger with state object actions
  useEffect(() => {
    dispatch(getAllSpendingProjectList());
  }, [dispatch]);

  useEffect(() => {
    const fetchFilteredData = () => {
      if (projectsFilterState === "Current") {
        const currentProjects = spendingProjectsData?.filter(
          (project) => project?.status === "Current"
        );
        setFilteredSpendingProjectsData(currentProjects);
      }

      if (projectsFilterState === "Ongoing") {
        const ongoingProjects = spendingProjectsData?.filter(
          (project) => project?.status === "Ongoing"
        );
        setFilteredSpendingProjectsData(ongoingProjects);
      }

      if (projectsFilterState === "Completed") {
        const completedProjects = spendingProjectsData?.filter(
          (project) => project?.status === "Completed"
        );
        setFilteredSpendingProjectsData(completedProjects);
      }
    };

    fetchFilteredData();
  }, [projectsFilterState, spendingProjectsData]);

  const handleFilterProjects = (filterState: "Current" | "Ongoing" | "Completed") => {
    setProjectsFilterState(filterState);
    goToTop();
  };

  // for table actions
  const tableActionBody = (rowData: ProjectSpendingTypes) => {
    const projectData: ProjectSpendingTypes = rowData;
    return (
      <div className="tabel_btn_area mt-1 mb-1">
        {/* {(rowData?.can_edit===true ) && ( */}
        <Button
          icon={`bi bi-plus-circle`}
          label={"Add"}
          disabled={(rowData?.can_edit === false || rowData?.can_edit === null)}
          className="tabel_btn view_btn fixed_width m-1"
          onClick={() => {
            navigate(`/project-spending`, {
              state: { projectData: projectData, openStatus: 1 },
            });
          }}
        />
        {/* )}  */}

        <Button
          icon={`bi bi-eye`}
          label={"View"}
          className="tabel_btn view_btn fixed_width"
          onClick={() => {
            navigate(`/project-spending`, {
              state: { projectData: projectData, openStatus: 0 },
            });
          }}
          disabled={projectData && projectData?.m_purchase!.length === 0}
        />
      </div>
    );
  };

  // client account details share contents
  const sharedContactBody = (rowData: ProjectSpendingTypes) => {
    return (
      <React.Fragment>
        {(rowData.shared_by != null) ? (<SharedByAvatar
          avatarKey={rowData?.shared_by?.id}
          firstName={rowData?.shared_by?.first_name}
          lastName={rowData?.shared_by?.last_name}
          profileImage={rowData?.shared_by?.profile_image}
        />) : (
          <>
            <h6>---</h6>
          </>
        )}
      </React.Fragment>

    );
  };


  /* ******************************* Add project ******************************* */

  // close button action pop up close
  const handleCloseProjectModal = () => {
    setShowAddProjectsModal(false);
    setSelectedProjects([]);
  };

  // open model for add projects
  const handleAddProject = () => {
    setShowAddProjectsModal(true);
  };

  function checkCredentials(): boolean {
    return selectedProjects.length > 0;
  }

  const addProjects = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);

      const data = await addProjectForSpending(selectedProjects);

      if (data.statusCode === 200) {
        // calling fromstate objct value
        dispatch(getAllSpendingProjectList());

        setLoading(false);
        handleCloseProjectModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Project(s) added successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error adding the project.",
            life: 3000,
          });
        }
      }
    }
  };

  const projectModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        Add project(s)
      </h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseProjectModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const projectModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseProjectModal}
      />

      <Button
        disabled={!checkCredentials()}
        label={`${loading ? "Processing" : "Add"}`}
        onClick={addProjects}
        loading={loading}
        className="custom_btn primary"
      />
    </div>
  );

  const handleSelectProjects = (project: ProjectSpendingTypes, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProjects((prevState) => [...prevState, project]);
    } else {
      setSelectedProjects((prevState) =>
        prevState.filter(
          (existingProject) => existingProject.project_id !== project.project_id
        )
      );
    }
  };

  const isSelectedProject = (project: ProjectSpendingTypes) =>
    selectedProjects.some(
      (existingproject) => existingproject?.project_id === project?.project_id
    );
  /* *************************************************************************** */


  /* ***************************** Common function ***************************** */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="main_heading">Project spending <span className="text_empty">- Projects</span></h4>
              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area mt-sm-3 mt-3">
              <div className="contact_tabs">
                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${projectsFilterState === "Current" ? "active" : ""} p-ripple`}
                    onClick={() => handleFilterProjects("Current")}
                  >
                    Current
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>
                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${projectsFilterState === "Ongoing" ? "active" : ""} p-ripple`}
                    onClick={() => handleFilterProjects("Ongoing")}
                  >
                    Ongoing
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>
                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${projectsFilterState === "Completed" ? "active" : ""} p-ripple`}
                    onClick={() => handleFilterProjects("Completed")}
                  >
                    Completed
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>
              </div>

              {is_permision_add_project && (
                <div className="page_action_area">
                  <button
                    className="add_data_link_btn p-ripple"
                    onClick={handleAddProject}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add project</span>
                    <Ripple />
                  </button>
                </div>
              )}
            </div>

            <div className="page_main_content_section p-0">
              {filteredSpendingProjectsData && filteredSpendingProjectsData?.length > 0 ? (
                <DataTable
                  value={filteredSpendingProjectsData}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No project(s) found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                  scrollable
                >
                  <Column
                    header="Job no."
                    headerClassName="text_purple"
                    field="job_number"
                    body={(rowData: ProjectSpendingTypes) => (
                      <span className="text-capitalize text_bold">
                        {rowData?.job_number ? rowData?.job_number : "---"}
                      </span>
                    )}
                    style={{ width: "15%" }}
                  ></Column>

                  <Column
                    header="Type"
                    headerClassName="text_purple"
                    field="project_type"
                    body={(rowData: ProjectSpendingTypes) => (
                      <span className="text-capitalize">
                        {rowData?.project_type === "CP" ? "Commercial" : "Domestic"}
                      </span>
                    )}
                    style={{ width: "15%" }}
                  ></Column>

                  <Column
                    header="Address"
                    headerClassName="text_purple"
                    field="address"
                    body={(rowData: ProjectSpendingTypes) => (
                      <span className="text-capitalize">
                        {rowData?.address ? rowData?.address : "---"}
                      </span>
                    )}
                    style={{ width: "25%" }}
                  ></Column>

                  <Column
                    header="Description"
                    headerClassName="text_purple"
                    field="description"
                    className="first_letter_capitalized"
                    body={(rowData: ProjectSpendingTypes) => (
                      <span>
                        {rowData?.description ? `${rowData.description.slice(0, 60)}${rowData.description?.length > 60 ? '...' : ''}` : "---"}
                      </span>
                    )}
                    style={{ width: "30%" }}
                  ></Column>

                  <Column
                    body={sharedContactBody}
                    header="Shared by"
                    headerClassName="text_purple"
                    className="text-center"
                    alignHeader={'center'}
                    style={{ width: "8%" }}
                  ></Column>

                  <Column
                    body={tableActionBody}
                    alignHeader="center"
                    className="table_btn_area"
                    frozen={window.innerWidth > 575}
                    alignFrozen="right"
                  ></Column>
                </DataTable>
              ) : (
                <NoDataCreated message={`No project(s) found.`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Project selection modal */}
      <Dialog
        visible={showAddProjectsModal}
        headerClassName="custom_modal_header"
        header={projectModalHeader}
        footer={projectModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseProjectModal}
      >
        <div className="custom_modal_body">
          <div className="p-1">
            {projectsData && projectsData?.length > 0 ? (
              <div className="row">
                <div className="col-12">
                  <label className={`custom_form_label mb-2`}>
                    Select project(s)
                  </label>
                  {projectsData?.map((project) => (
                    <div
                      className="option_checkbox_area"
                      key={project?.project_id}
                    >
                      <Checkbox
                        onChange={(e) => handleSelectProjects(project, e.checked || false)}
                        checked={isSelectedProject(project)}
                      ></Checkbox>

                      <Tooltip
                        id={`project_type${project?.project_id}`}
                        key={`project_type${project?.project_id}`}
                        target={`.project_type${project?.project_id}`}
                      />

                      <article
                        className={`shared_contact_body selectable mb-0 ${isSelectedProject(project) && "selected"} p-ripple`}
                        onClick={() => handleSelectProjects(project, !isSelectedProject(project))}
                      >
                        <h6 className="text-capitalize">
                          {project?.job_number}: {project?.address}
                        </h6>
                        <h6
                          className={`project_type${project?.project_id}`}
                          data-pr-tooltip={project?.project_type === "CP" ? "Commercial project" : "Domestic project"}
                          data-pr-position="top"
                        >
                          {project?.project_type}
                        </h6>
                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no_records_found">
                <i className="bi bi-exclamation-circle me-1"></i>
                Project(s) not available.
              </div>
            )}
          </div>
        </div>
      </Dialog>
      {/*  */}
    </React.Fragment>
  );
};

export default ProjectSpendingProjects;
